import { SearchOutlined } from '@ant-design/icons';
import { type CheckboxChangeEvent } from 'antd/lib/checkbox';
import { type InputRef, Radio } from 'antd5';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomFilterContainer,
  CustomFilterPopover,
  FilterActions,
  FilterSearchButton,
  FilterSearchInput,
  SecondaryFilterLabel,
} from 'components/data/helpers/filters.style';
import { Button } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { AtlasGqlConfirmationStatus } from 'types/atlas-graphql';

const noneValue = ' ';
const confirmedValue = [AtlasGqlConfirmationStatus.Confirmed];
const unconfirmedValue = [
  AtlasGqlConfirmationStatus.ConfirmedUpdated,
  AtlasGqlConfirmationStatus.NotConfirmed,
];
type DamageFilterKey = Extract<CustomFilterDropdownProps['selectedKeys'][number], { key: string }>;

export const DamageIdAndStatusFilter: React.FunctionComponent<CustomFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const inputRef = useRef<InputRef>(null);
  const { t } = useTranslation();
  const damageFilters = selectedKeys as DamageFilterKey[];

  const [damageIdValue] = (damageFilters.find(({ key }) => key === 'id')?.value ?? ['']) as string;
  const confirmationStatusValue = damageFilters.find(
    ({ key }) => key === 'confirmationStatus'
  )?.value;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue: string = [e.target.value].find(Boolean) ?? '';
    setSelectedKeys([
      ...damageFilters.filter(({ key }) => key !== 'id'),
      { key: 'id', value: [newValue] },
    ]);
  };

  const handleSubmit = () => confirm({ closeDropdown: true });
  const handleReset = () => {
    setSelectedKeys([]);
    clearFilters && clearFilters();
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleReset();
    }
  };

  return (
    <AutoFocus elementToFocus={inputRef}>
      <CustomFilterPopover
        className="ant-table-filter-dropdown"
        role="dialog"
        aria-label={t('common.filter', 'Filter')}
        onKeyUp={handleKeyUp}
      >
        <CustomFilterContainer>
          <FilterSearchInput
            ref={inputRef}
            aria-label={t('common.search', 'Search')}
            onChange={handleSearchChange}
            onPressEnter={handleSubmit}
            placeholder={t('common.search', 'Search')}
            suffix={
              <FilterSearchButton
                type="button"
                aria-label={t('common.search', 'Search')}
                onClick={handleSubmit}
              >
                <SearchOutlined aria-hidden />
              </FilterSearchButton>
            }
            value={damageIdValue}
          />
        </CustomFilterContainer>
        <CustomFilterContainer>
          <SecondaryFilterLabel>
            {t('table.confirmationStatus', 'Confirmation Status')}
          </SecondaryFilterLabel>
          <Radio.Group
            options={[
              { label: t('table.confirmed', 'Confirmed'), value: true },
              { label: t('table.unconfirmed', 'Unconfirmed'), value: false },
              { label: t('table.all', 'All'), value: noneValue },
            ]}
            onChange={({ target }: CheckboxChangeEvent) => {
              if (target.value === true) {
                setSelectedKeys([
                  ...damageFilters.filter(({ key }) => key !== 'confirmationStatus'),
                  { key: 'confirmationStatus', value: confirmedValue },
                ]);
              } else if (target.value === false) {
                setSelectedKeys([
                  ...damageFilters.filter(({ key }) => key !== 'confirmationStatus'),
                  { key: 'confirmationStatus', value: unconfirmedValue },
                ]);
              } else {
                setSelectedKeys([
                  ...damageFilters.filter(({ key }) => key !== 'confirmationStatus'),
                ]);
              }
            }}
            value={
              confirmationStatusValue === confirmedValue
                ? true
                : confirmationStatusValue === unconfirmedValue
                  ? false
                  : noneValue
            }
            optionType="button"
          />
        </CustomFilterContainer>

        <FilterActions>
          <Button type="link" size="small" onClick={handleReset}>
            {t('table.reset', 'Reset')}
          </Button>
          <Button type="primary" size="small" onClick={handleSubmit}>
            {t('table.ok', 'OK')}
          </Button>
        </FilterActions>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
